/* eslint-disable prefer-destructuring, consistent-return */
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useReducer } from 'react'

const INTERSECTION_THRESHOLD = 5
const LOAD_DELAY_MS = 500

const useLazyLoad = ({ triggerRef, loadData, options, dataLength }) => {
  const [state, dispatch] = useReducer(reducer, {
    loading: false,
    currentPage: 1,
    data: [],
  })

  /* eslint-disable no-underscore-dangle */
  const _handleEntry = async entry => {
    // eslint-disable-line prefer-destructuring
    const boundingRect = entry.boundingClientRect
    const intersectionRect = entry.intersectionRect

    if (
      !state.loading &&
      entry.isIntersecting &&
      intersectionRect.bottom - boundingRect.bottom <= INTERSECTION_THRESHOLD
    ) {
      dispatch({ type: 'set', payload: { loading: true } })
      const data = await loadData(state.currentPage)
      dispatch({ type: 'loadData', payload: { data } })
    }
  }
  /* eslint-enable no-underscore-dangle */

  const handleEntry = debounce(_handleEntry, LOAD_DELAY_MS)
  const onIntersect = useCallback(
    entries => {
      if (state.data.length >= dataLength) return
      handleEntry(entries[0])
    },
    [handleEntry]
  )

  useEffect(() => {
    if (triggerRef.current) {
      const container = triggerRef.current
      const observer = new IntersectionObserver(onIntersect, options)

      observer.observe(container)

      return () => {
        observer.disconnect()
      }
    }
  }, [triggerRef, onIntersect, options])

  return state
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'set': {
      return {
        ...state,
        ...action.payload,
      }
    }
    case 'loadData': {
      return {
        ...state,
        loading: false,
        data: [...state.data, ...action.payload.data],
        currentPage: state.currentPage + 1,
      }
    }
    default:
      return state
  }
}

export default useLazyLoad
